import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { AdminCommandPalette } from '@apps/web/components/admin/admin_command_palette';
import { ModeToggle } from '@apps/web/components/mode_toggle';
import { type AgentComparison } from '@apps/web/components/phoebe_chat/agent_test_mode';
import {
    DEFAULT_PHOEBE_CHAT_DEBUG_TAB,
    PHOEBE_CHAT_DEBUG_TAB_VALUES,
    PhoebeChatDebugSidebar,
    type PhoebeChatDebugTab,
} from '@apps/web/components/phoebe_chat_debug_sidebar';
import { type PhoebeChatDebugSnapshot } from '@apps/web/components/phoebe_chat_debug_snapshot';
import {
    PhoebeChatView,
    StructuredReportArtifactsPanel,
} from '@apps/web/components/phoebe_chat_view';
import { Button } from '@apps/web/components/ui/button';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const DEFAULT_CHAT_PANEL_SIZE = 62;
const DEFAULT_DEBUG_PANEL_SIZE = 38;
const MIN_DEBUG_PANEL_SIZE = 20;

export const agentSearchSchema = z.object({
    chatId: z.string().optional(),
    debugTab: z.enum(PHOEBE_CHAT_DEBUG_TAB_VALUES).optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export const Route = createFileRoute('/_app/(agent)/agent')({
    component: AgentDebugPage,
    validateSearch: agentSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Agent Debug | Phoebe',
            },
        ],
    }),
});

function AgentDebugPage() {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'agent-debug-page',
    });
    const { chatId: urlChatId, debugTab } = Route.useSearch();
    const navigate = Route.useNavigate();
    const [chatId, setChatId] = useState<string | null>(urlChatId ?? null);
    const [chatDebugSnapshot, setChatDebugSnapshot] =
        useState<PhoebeChatDebugSnapshot | null>(null);
    const [comparisonChatId, setComparisonChatId] = useState<string | null>(
        null,
    );
    const [comparisonDebugSnapshot, setComparisonDebugSnapshot] =
        useState<PhoebeChatDebugSnapshot | null>(null);
    const [composerPortalTarget, setComposerPortalTarget] =
        useState<HTMLDivElement | null>(null);

    useEffect(() => {
        setChatId(urlChatId ?? null);
    }, [urlChatId]);

    const handleSelectedConversationIdChange = useCallback(
        (nextChatId: string | null) => {
            setChatId(nextChatId);
            setComparisonChatId(null);
            setComparisonDebugSnapshot(null);
            void navigate({
                to: '/agent',
                search: (previous) => ({
                    ...previous,
                    chatId: nextChatId ?? undefined,
                }),
                replace: true,
            });
        },
        [navigate],
    );
    const handleComparisonConversationChange = useCallback(
        (comparison: AgentComparison | null) => {
            if (comparison?.role === 'raw') {
                handleSelectedConversationIdChange(comparison.runId);
                return;
            }
            setComparisonChatId(comparison?.runId ?? null);
            if (comparison === null) {
                setComparisonDebugSnapshot(null);
            }
        },
        [handleSelectedConversationIdChange],
    );

    const handleDebugTabChange = useCallback(
        (nextTab: PhoebeChatDebugTab) => {
            void navigate({
                to: '/agent',
                search: (previous) => ({
                    ...previous,
                    debugTab:
                        nextTab === DEFAULT_PHOEBE_CHAT_DEBUG_TAB
                            ? undefined
                            : nextTab,
                }),
                replace: true,
            });
        },
        [navigate],
    );

    return (
        <div className="bg-background flex h-full min-h-0 flex-col overflow-hidden">
            <AdminCommandPalette />
            <header className="flex shrink-0 items-center gap-3 border-b px-3 py-2">
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/home">
                        <ArrowLeft className="size-4" />
                        Back to app
                    </Link>
                </Button>
                <div className="min-w-0 flex-1">
                    <h1 className="text-sm font-semibold">Agent debug</h1>
                    <p className="text-muted-foreground truncate text-xs">
                        {organization?.name ?? 'Loading organization…'}
                    </p>
                </div>
                <div className="w-44">
                    <ModeToggle />
                </div>
            </header>
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <ResizablePanelGroup
                    orientation="horizontal"
                    className="h-full min-h-0 w-full overflow-hidden"
                    defaultLayout={{
                        'chat-panel': DEFAULT_CHAT_PANEL_SIZE,
                        'debug-panel': DEFAULT_DEBUG_PANEL_SIZE,
                    }}
                >
                    <ResizablePanel
                        id="chat-panel"
                        defaultSize={DEFAULT_CHAT_PANEL_SIZE}
                        className="min-h-0 overflow-hidden"
                    >
                        <div className="flex h-full min-h-0 flex-col">
                            <div className="flex min-h-0 flex-1">
                                <section className="flex min-w-0 flex-1 flex-col">
                                    {comparisonChatId ? (
                                        <div className="bg-muted/40 shrink-0 border-b px-3 py-1.5 text-xs font-semibold">
                                            Normal
                                        </div>
                                    ) : null}
                                    <div className="min-h-0 flex-1">
                                        <PhoebeChatView
                                            target={{ kind: 'general' }}
                                            mode="general"
                                            conversationId={chatId}
                                            onSelectedConversationIdChange={
                                                handleSelectedConversationIdChange
                                            }
                                            onComparisonConversationChange={
                                                handleComparisonConversationChange
                                            }
                                            onDebugSnapshotChange={
                                                setChatDebugSnapshot
                                            }
                                            debugPanelOpen={true}
                                            parallelAgentTestingEnabled={true}
                                            composerPortalTarget={
                                                comparisonChatId
                                                    ? composerPortalTarget
                                                    : null
                                            }
                                        />
                                    </div>
                                </section>
                                {comparisonChatId ? (
                                    <section className="flex min-w-0 flex-1 flex-col border-l">
                                        <div className="bg-muted/40 shrink-0 border-b px-3 py-1.5 text-xs font-semibold">
                                            Raw
                                        </div>
                                        <div className="min-h-0 flex-1">
                                            <PhoebeChatView
                                                target={{ kind: 'general' }}
                                                mode="general"
                                                conversationId={
                                                    comparisonChatId
                                                }
                                                readOnly={true}
                                                isVisible={false}
                                                onDebugSnapshotChange={
                                                    setComparisonDebugSnapshot
                                                }
                                                debugPanelOpen={true}
                                            />
                                        </div>
                                    </section>
                                ) : null}
                            </div>
                            <div
                                ref={setComposerPortalTarget}
                                data-testid="parallel-composer-host"
                                className={
                                    comparisonChatId ? 'shrink-0' : 'hidden'
                                }
                            />
                        </div>
                    </ResizablePanel>
                    <ResizableHandle withHandle={true} />
                    <ResizablePanel
                        id="debug-panel"
                        defaultSize={DEFAULT_DEBUG_PANEL_SIZE}
                        minSize={MIN_DEBUG_PANEL_SIZE}
                        className="min-h-0 overflow-hidden"
                    >
                        <PhoebeChatDebugSidebar
                            snapshot={chatDebugSnapshot}
                            comparisonSnapshot={comparisonDebugSnapshot}
                            comparisonEnabled={comparisonChatId !== null}
                            tab={debugTab ?? DEFAULT_PHOEBE_CHAT_DEBUG_TAB}
                            onTabChange={handleDebugTabChange}
                        />
                    </ResizablePanel>
                </ResizablePanelGroup>
                <StructuredReportArtifactsPanel splitMode={false} />
            </div>
        </div>
    );
}
